import React, { useState, useEffect, useContext, useCallback } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import ViewUser from './ViewUser';
import NewUserForm from './NewUserForm';
import TableMenu from '../TableMenu';
import Tabs from '../Tabs';
import classes from './styles.module.scss';
import AdminService from '../../../../services/AdminService';
import EditUser from './EditUser';
import Table from '../../../Table';
import ChangePasswordModal from '../../../../modals/ChangePasswordModal';

import { useUserStore } from '../../../../store';
import { UiContext } from '../../../../context/UiContext';
import tableColsToSortOptions from '../../../../helpers/tableColsToSortOptions';

let timer;

export default function UserManagementSimple() {
  const [activeAction, setActiveAction] = useState('');
  const [activeUsersSortOption, setActiveUsersSortOption] = useState({
    label: 'Created On',
    value: 'createdAt',
  });
  const [selectedRows, setSelectedRows] = useState([]);
  const [usersSearchTerm, setUsersSearchTerm] = useState('');
  const [debouncedUsersSearchTerm, setDebouncedUsersSearchTerm] = useState('');
  const [isAddUserFormVisible, setIsAddUserFormVisible] = useState(false);
  const [isViewUserVisible, setIsViewUserVisible] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [isEditUserVisible, setIsEditUserVisible] = useState(false);
  const [usersSortOrder, setUsersSortOrder] = useState('desc');
  const [searchHistory, setSearchHistory] = useState([]);
  const [isChangePasswordModalVisible, setIsChangePasswordModalVisible] =
    useState(false);

  // Pagination
  const [usersPage, setUsersPage] = useState(1);

  const { t } = useTranslation();

  const scopes = useUserStore((state) => state.userScopes);
  const isAdmin =
    scopes.includes('api:full') ||
    scopes.includes('api:root') ||
    scopes.includes('api:admin:auth');

  const tabs = [
    {
      id: 'users',
      label: t('dashboardComponents.Console.UserManagement.tabs.users'),
    },
  ];
  const [currentTab, setCurrentTab] = useState('users');

  const usersCols = [
    {
      field: 'userProfile.firstName',
      name: t(
        'dashboardComponents.Console.UserManagement.columns.users.firstName'
      ),
      sortable: true,
    },
    {
      field: 'userProfile.middleName',
      name: t(
        'dashboardComponents.Console.UserManagement.columns.users.middleName'
      ),
      sortable: true,
    },
    {
      field: 'userProfile.lastName',
      name: t(
        'dashboardComponents.Console.UserManagement.columns.users.lastName'
      ),
      sortable: true,
    },
    {
      field: 'userProfile.designation',
      name: t(
        'dashboardComponents.Console.UserManagement.columns.users.designation'
      ),
      sortable: true,
    },
    {
      field: 'userName',
      name: t('dashboardComponents.Console.UserManagement.columns.users.email'),
      sortable: true,
    },
    {
      field: 'userProfile.phone',
      name: t('dashboardComponents.Console.UserManagement.columns.users.phone'),
      sortable: true,
    },
    {
      field: 'roles',
      name: t('dashboardComponents.Console.UserManagement.columns.users.role'),
      type: 'role',
      sortable: true,
      sortField: 'firstRoleName',
    },
    {
      field: 'createdAt',
      name: t('common.createdOn'),
      sortable: true,
      type: 'date',
    },
  ];

  const sortUsersOptions = tableColsToSortOptions(usersCols);

  const {
    showModal,
    showUnknownErrorModal,
    showSelectionNeededModal,
    setIsFetching,
    showConflictModal,
  } = useContext(UiContext);

  const { data: roles } = useQuery({
    queryKey: ['roles'],
    queryFn: () =>
      AdminService.getRoles({
        orderBy: 'name',
        pageNumber: 1,
        pageSize: 100,
      }),
    keepPreviousData: true,
  });

  const {
    data: users,
    refetch: refetchUsers,
    isFetching: areUsersFetching,
  } = useQuery({
    queryKey: ['users', usersPage],
    queryFn: async () => {
      const response = await AdminService.getUsers({
        orderBy: activeUsersSortOption.value || 'id',
        term: debouncedUsersSearchTerm,
        sortOrder: usersSortOrder,
        pageNumber: usersPage,
      });

      return response;
    },
    keepPreviousData: true,
  });

  useEffect(() => {
    refetchUsers();
  }, [
    activeUsersSortOption,
    debouncedUsersSearchTerm,
    refetchUsers,
    usersSortOrder,
  ]);

  let isFetching = false;
  let debouncedSearchTerm = '';
  let currentDataSource = {};

  if (currentTab === 'users') {
    currentDataSource = users;
    isFetching = areUsersFetching;
    debouncedSearchTerm = debouncedUsersSearchTerm;
  }

  const deleteUsers = () => {
    showModal({
      icon: 'delete',
      title: t(
        'dashboardComponents.Console.UserManagement.modals.deleteUser.title'
      ),
      text: t(
        'dashboardComponents.Console.UserManagement.modals.deleteUser.confirmationText'
      ),
      onConfirm: async () => {
        try {
          setIsFetching(true);
          const promises = selectedRows.map((row) =>
            AdminService.deleteUser({ id: row })
          );
          await Promise.all(promises);
          await refetchUsers();
        } catch (error) {
          console.log(error);

          if (error.response.status === 409) {
            showConflictModal(error.response.data.message);
          } else {
            showUnknownErrorModal();
          }
        } finally {
          setIsFetching(false);
        }
      },
      onCancel: () => {},
      dismissButtonLabel: t('common.no'),
      confirmButtonLabel: t('common.yes'),
    });
  };

  const showViewUser = (id) => {
    setCurrentUserId(id);
    setIsViewUserVisible(true);
    setTimeout(() => {
      setCurrentUserId(null);
    }, 0);
  };

  const getSearchHistory = useCallback(() => {
    const savedSearchHistory = localStorage.getItem('searchHistory');

    if (!savedSearchHistory) {
      return [];
    }

    try {
      return JSON.parse(savedSearchHistory);
    } catch (error) {
      return [];
    }
  }, []);

  const saveSearchHistory = useCallback(
    (term) => {
      const history = getSearchHistory();

      if (history.includes(term)) {
        return;
      }

      if (history.length === 4) {
        history.pop();
      }

      history.unshift(term);
      localStorage.setItem('searchHistory', JSON.stringify(history));
      setSearchHistory(history);
    },
    [getSearchHistory]
  );

  useEffect(() => {
    const history = getSearchHistory();
    setSearchHistory(history);
  }, [getSearchHistory]);

  useEffect(() => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      setDebouncedUsersSearchTerm(usersSearchTerm);
      if (usersSearchTerm.trim()) {
        saveSearchHistory(usersSearchTerm);
      }
    }, 500);
  }, [saveSearchHistory, usersSearchTerm]);

  useEffect(() => {
    setSelectedRows([]);
    setActiveAction('');
  }, [currentTab]);

  const defaultUsersActions = [
    {
      label: t('common.addUser'),
      value: 'Invite',
      onClick: () => {
        setIsAddUserFormVisible(true);
      },
    },
    {
      label: t('common.edit'),
      value: 'Edit',
      onClick: () => {
        if (selectedRows.length) {
          setIsEditUserVisible(true);
        } else {
          showSelectionNeededModal();
        }
      },
    },
    {
      label: t('common.delete'),
      value: 'Delete',
      onClick: () => {
        if (selectedRows.length) {
          deleteUsers();
        } else {
          showSelectionNeededModal();
        }
      },
    },
  ];

  const adminUsersActions = [
    {
      label: t('common.changePassword'),
      value: 'ChangePassword',
      onClick: () => {
        if (selectedRows.length > 1) {
          showModal({
            icon: 'warning',
            title: t(
              'dashboardComponents.Console.UserManagement.modals.reduceSelection.title'
            ),
            text: t(
              'dashboardComponents.Console.UserManagement.modals.reduceSelection.text'
            ),
            dismissButtonVariant: 'modal',
            dismissButtonLabel: t('common.gotIt'),
          });
        } else {
          setIsChangePasswordModalVisible(true);
        }
      },
    },
  ];

  const usersActions = isAdmin
    ? [...defaultUsersActions, ...adminUsersActions]
    : defaultUsersActions;

  let actions;
  let sortOptions;
  let activeSortOption;
  let setActiveSortOption;
  let sortOrder;
  let setSortOrder;
  let searchTerm;
  let setSearchTerm;

  if (currentTab === 'users') {
    actions = usersActions;
    sortOptions = sortUsersOptions;
    activeSortOption = activeUsersSortOption;
    setActiveSortOption = setActiveUsersSortOption;
    sortOrder = usersSortOrder;
    setSortOrder = setUsersSortOrder;
    searchTerm = usersSearchTerm;
    setSearchTerm = setUsersSearchTerm;
  }

  // Pagination variables
  let currentPage = 0;
  let setCurrentPage = () => {};
  let totalPages = 0;

  if (currentTab === 'users') {
    currentPage = usersPage;
    totalPages = users?.totalPages || 0;
    setCurrentPage = setUsersPage;
  }

  return (
    <div className={classes.UserManagement}>
      <Tabs
        name="user"
        currentTab={currentTab}
        setCurrentTab={setCurrentTab}
        tabs={tabs}
        countData={{ users: users?.totalCount || 0 }}
      />
      <div className={classes.tableContainer}>
        <TableMenu
          actions={actions}
          activeAction={activeAction}
          setActiveAction={setActiveAction}
          dangerAction="Delete"
          sortOptions={sortOptions}
          activeSortOption={activeSortOption}
          setActiveSortOption={setActiveSortOption}
          selectedCount={selectedRows.length}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          searchHistory={searchHistory}
          desktopLayoutOnMobile
          setSortOrder={setSortOrder}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={totalPages}
          searchResultCount={
            debouncedSearchTerm && !isFetching
              ? currentDataSource?.totalCount
              : null
          }
        />

        {currentTab === 'users' && (
          <Table
            name={t('dashboardComponents.Console.UserManagement.tabs.users')}
            isFetching={areUsersFetching}
            onRowClick={showViewUser}
            rows={users?.data || []}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            sortOptions={sortOptions}
            setActiveSortOption={setActiveUsersSortOption}
            activeSortOption={activeSortOption}
            setSortOrder={setSortOrder}
            sortOrder={sortOrder}
            columns={usersCols}
          />
        )}
        <NewUserForm
          isVisible={isAddUserFormVisible}
          handleClose={() => setIsAddUserFormVisible(false)}
          refetch={refetchUsers}
          setIsAddUserFormVisible={setIsAddUserFormVisible}
          roles={roles?.data || []}
        />

        <ViewUser
          isVisible={isViewUserVisible}
          handleClose={() => setIsViewUserVisible(false)}
          users={users?.data || []}
          currentUserId={currentUserId}
          refetch={refetchUsers}
          setIsEditUserVisible={setIsEditUserVisible}
          setSelectedRows={setSelectedRows}
          isOverlayed={isEditUserVisible}
        />

        <EditUser
          isVisible={isEditUserVisible}
          users={users?.data || []}
          selectedRows={selectedRows}
          handleClose={() => setIsEditUserVisible(false)}
          refetch={refetchUsers}
          noAnimation={isViewUserVisible}
        />

        <ChangePasswordModal
          show={isChangePasswordModalVisible}
          handleClose={() => setIsChangePasswordModalVisible(false)}
          currentUser={users?.data.find((user) => user.id === selectedRows[0])}
        />
      </div>
    </div>
  );
}
